import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Info } from 'lucide-react';

export interface PFSDQGeneralAnswers {
  q1: boolean | null;
  q2_per_month: string;
  q3: number | null;
  q4: number | null;
  q5: number | null;
}

export interface PFSDQChangeActivity {
  value: number | null;
  never: boolean;
}

export interface PFSDQMResultPayload {
  dyspnea: {
    general: PFSDQGeneralAnswers;
    activities: (number | null)[];
    score: number;
  };
  fatigue: {
    general: PFSDQGeneralAnswers;
    activities: (number | null)[];
    score: number;
  };
  change: {
    activities: PFSDQChangeActivity[];
    score: number;
  };
  manualCounts: {
    dyspneaAnswered: number;
    fatigueAnswered: number;
    changeAnswered: number;
  };
  total: number;
  finishedAt?: string;
}

interface PFSDQMTestProps {
  initial?: PFSDQMResultPayload | null;
  onCancel?: () => void;
  onNext: (result: PFSDQMResultPayload) => void;
}

export const PFSDQ_ACTIVITIES = [
  'Escovar os cabelos',
  'Levantar os braços acima da cabeça',
  'Tomar banho',
  'Lavar os cabelos',
  'Vestir camisa',
  'Preparar lanche',
  'Andar em plano inclinado',
  'Subir 3 degraus',
  'Andar 3 metros',
  'Andar em terrenos acidentados',
] as const;

const scoreFrom = (values: (number | null)[]) => {
  const answered = values.filter((value) => value !== null) as number[];
  if (!answered.length) return 0;
  const average = answered.reduce((sum, value) => sum + value, 0) / answered.length;
  return Math.round(average * 10);
};

function Scale0to10({
  name,
  value,
  onChange,
  disabled = false,
}: {
  name: string;
  value: number | null;
  onChange: (value: number | null) => void;
  disabled?: boolean;
}) {
  return (
    <div className="grid grid-cols-11 overflow-hidden rounded-lg border border-slate-300">
      {Array.from({ length: 11 }).map((_, index) => (
        <label
          key={index}
          className={`cursor-pointer select-none border-r border-slate-300 py-1 text-center text-sm last:border-r-0 ${
            disabled
              ? 'cursor-not-allowed bg-slate-100 text-slate-400'
              : value === index
                ? 'bg-blue-600 text-white'
                : 'bg-white text-slate-700 hover:bg-slate-50'
          }`}
        >
          <input
            type="radio"
            className="sr-only"
            name={name}
            checked={value === index}
            onChange={() => !disabled && onChange(index)}
            disabled={disabled}
            aria-label={`${name}-${index}`}
          />
          {index}
        </label>
      ))}
    </div>
  );
}

function LegendHeader({ labels }: { labels: string[] }) {
  return (
    <div className="grid grid-cols-5 text-[10px] text-slate-600">
      {labels.map((label, index) => (
        <div key={label} className={`px-1 text-center ${index !== 0 ? 'border-l border-slate-300' : ''}`}>
          {label}
        </div>
      ))}
    </div>
  );
}

function ActivityRow({
  index,
  label,
  value,
  onChange,
  headerLabels,
}: {
  index: number;
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  headerLabels: string[];
}) {
  return (
    <div className="space-y-1.5 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
      <LegendHeader labels={headerLabels} />
      <div className="grid grid-cols-11 text-xs text-slate-500">
        {Array.from({ length: 11 }).map((_, item) => (
          <div key={item} className="text-center">
            {item}
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-slate-200">
        <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-800">
          {index + 1}. {label}
        </div>
        <div className="p-3">
          <Scale0to10 name={`pfsdq_row_${index}`} value={value} onChange={onChange} />
        </div>
      </div>
    </div>
  );
}

function GeneralQuestionScale({
  name,
  value,
  onChange,
  legend,
}: {
  name: string;
  value: number | null;
  onChange: (value: number | null) => void;
  legend: string[];
}) {
  return (
    <div>
      <LegendHeader labels={legend} />
      <div className="my-1 grid grid-cols-11 text-xs text-slate-500">
        {Array.from({ length: 11 }).map((_, index) => (
          <div key={index} className="text-center">
            {index}
          </div>
        ))}
      </div>
      <Scale0to10 name={name} value={value} onChange={onChange} />
    </div>
  );
}

export default function PFSDQMTest({ initial, onCancel, onNext }: PFSDQMTestProps) {
  const [dQ1, setDQ1] = useState<boolean | null>(null);
  const [dQ2, setDQ2] = useState('');
  const [dQ3, setDQ3] = useState<number | null>(null);
  const [dQ4, setDQ4] = useState<number | null>(null);
  const [dQ5, setDQ5] = useState<number | null>(null);
  const [dAct, setDAct] = useState<(number | null)[]>(Array(PFSDQ_ACTIVITIES.length).fill(null));

  const [fQ1, setFQ1] = useState<boolean | null>(null);
  const [fQ2, setFQ2] = useState('');
  const [fQ3, setFQ3] = useState<number | null>(null);
  const [fQ4, setFQ4] = useState<number | null>(null);
  const [fQ5, setFQ5] = useState<number | null>(null);
  const [fAct, setFAct] = useState<(number | null)[]>(Array(PFSDQ_ACTIVITIES.length).fill(null));

  const [changeActivities, setChangeActivities] = useState<PFSDQChangeActivity[]>(
    Array(PFSDQ_ACTIVITIES.length)
      .fill(null)
      .map(() => ({ value: null, never: false })),
  );

  const [step, setStep] = useState<0 | 1 | 2>(0);

  useEffect(() => {
    if (!initial) return;

    setDQ1(initial.dyspnea.general.q1 ?? null);
    setDQ2(initial.dyspnea.general.q2_per_month ?? '');
    setDQ3(initial.dyspnea.general.q3 ?? null);
    setDQ4(initial.dyspnea.general.q4 ?? null);
    setDQ5(initial.dyspnea.general.q5 ?? null);
    setDAct(Array.from({ length: PFSDQ_ACTIVITIES.length }, (_, index) => initial.dyspnea.activities[index] ?? null));

    setFQ1(initial.fatigue.general.q1 ?? null);
    setFQ2(initial.fatigue.general.q2_per_month ?? '');
    setFQ3(initial.fatigue.general.q3 ?? null);
    setFQ4(initial.fatigue.general.q4 ?? null);
    setFQ5(initial.fatigue.general.q5 ?? null);
    setFAct(Array.from({ length: PFSDQ_ACTIVITIES.length }, (_, index) => initial.fatigue.activities[index] ?? null));

    setChangeActivities(
      Array.from({ length: PFSDQ_ACTIVITIES.length }, (_, index) => {
        const current = initial.change.activities[index];
        return {
          value: current?.value ?? null,
          never: Boolean(current?.never),
        };
      }),
    );
    setStep(0);
  }, [initial]);

  const dyspneaScore = useMemo(() => scoreFrom(dAct), [dAct]);
  const fatigueScore = useMemo(() => scoreFrom(fAct), [fAct]);
  const changeScore = useMemo(
    () => scoreFrom(changeActivities.map((entry) => (entry.never ? null : entry.value))),
    [changeActivities],
  );
  const total = dyspneaScore + fatigueScore + changeScore;

  const manualCounts = {
    dyspneaAnswered: dAct.filter((value) => value != null).length,
    fatigueAnswered: fAct.filter((value) => value != null).length,
    changeAnswered: changeActivities.filter((entry) => !entry.never && entry.value != null).length,
  };

  const tabs = [
    { label: 'Dispneia', complete: manualCounts.dyspneaAnswered > 0 || dQ3 != null || dQ4 != null || dQ5 != null },
    { label: 'Fadiga', complete: manualCounts.fatigueAnswered > 0 || fQ3 != null || fQ4 != null || fQ5 != null },
    { label: 'Mudança nas atividades', complete: manualCounts.changeAnswered > 0 || changeActivities.some((entry) => entry.never) },
  ] as const;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-md bg-blue-50 p-3 text-blue-900">
        <Info className="mt-0.5 h-5 w-5" />
        <div>
          <p className="font-medium">PFSDQ-M — Status Funcional e Dispneia</p>
          <p className="text-sm opacity-90">Três domínios: Dispneia, Fadiga e Mudança nas atividades. Cada domínio vai de 0 a 100, com total de 0 a 300.</p>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 pt-4">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
            {tabs.map((tab, index) => {
              const isActive = step === index;
              return (
                <button
                  key={tab.label}
                  type="button"
                  onClick={() => setStep(index as 0 | 1 | 2)}
                  className={`rounded-xl border px-4 py-3 text-left transition ${
                    isActive
                      ? 'border-blue-600 bg-blue-600 text-white'
                      : 'border-slate-200 bg-slate-50 text-slate-800 hover:border-slate-300 hover:bg-slate-100'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-semibold">{tab.label}</div>
                    <div className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${isActive ? 'bg-white/15 text-white' : tab.complete ? 'bg-emerald-100 text-emerald-700' : 'bg-white text-slate-500'}`}>
                      {tab.complete ? 'Respondido' : `Etapa ${index + 1}`}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-5 px-4 py-4">
          {step === 0 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-center text-lg font-semibold text-slate-900">Avaliação da dispneia</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">
                  As questões a seguir são relacionadas ao seu <span className="font-semibold">desconforto respiratório</span>. Escolha a resposta mais precisa.
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="block">
                  <span className="text-sm">1. Você já sentiu falta de ar?</span>
                  <div className="mt-2 flex gap-4 text-sm">
                    <label className="inline-flex items-center gap-1"><input type="radio" name="pfsdq_dq1" checked={dQ1 === true} onChange={() => setDQ1(true)} /> Sim</label>
                    <label className="inline-flex items-center gap-1"><input type="radio" name="pfsdq_dq1" checked={dQ1 === false} onChange={() => setDQ1(false)} /> Não</label>
                  </div>
                </label>
                <label className="block">
                  <span className="text-sm">2. Quantas vezes por mês você sente falta de ar grave a muito grave?</span>
                  <input type="number" min={0} className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2" value={dQ2} onChange={(event) => setDQ2(event.target.value)} />
                </label>
              </div>

              <div className="space-y-4">
                <p className="text-sm text-slate-700">Usando a escala a seguir, marque de 0 (nenhuma falta de ar) a 10 (falta de ar muito grave) nas questões abaixo:</p>
                <div>
                  <div className="mb-1 text-sm">3. Como você se sentiu na <span className="font-semibold">maioria dos dias</span> durante o ano passado?</div>
                  <GeneralQuestionScale
                    name="pfsdq_dq3"
                    value={dQ3}
                    onChange={setDQ3}
                    legend={['Nenhuma falta de ar', 'Leve', 'Moderada', 'Grave', 'Muito grave']}
                  />
                </div>
                <div>
                  <div className="mb-1 text-sm">4. Como você se sente <span className="font-semibold">hoje</span>?</div>
                  <GeneralQuestionScale
                    name="pfsdq_dq4"
                    value={dQ4}
                    onChange={setDQ4}
                    legend={['Nenhuma falta de ar', 'Leve', 'Moderada', 'Grave', 'Muito grave']}
                  />
                </div>
                <div>
                  <div className="mb-1 text-sm">5. Como você se sente na <span className="font-semibold">maioria das atividades do dia a dia</span>?</div>
                  <GeneralQuestionScale
                    name="pfsdq_dq5"
                    value={dQ5}
                    onChange={setDQ5}
                    legend={['Nenhuma falta de ar', 'Leve', 'Moderada', 'Grave', 'Muito grave']}
                  />
                </div>
              </div>

              <div>
                <h3 className="text-center text-lg font-semibold text-slate-900">Grau de falta de ar nas atividades</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">
                  Classifique as atividades de 0 a 10 de acordo com o grau de falta de ar que cada uma geralmente causa. Deixe em branco atividades com que você nunca esteve envolvido.
                </p>
              </div>

              <div className="space-y-4">
                {PFSDQ_ACTIVITIES.map((activity, index) => (
                  <ActivityRow
                    key={`dyspnea-${activity}`}
                    index={index}
                    label={activity}
                    value={dAct[index]}
                    onChange={(value) => setDAct((current) => current.map((item, itemIndex) => (itemIndex === index ? value : item)))}
                    headerLabels={['Nenhuma', 'Leve', 'Moderada', 'Grave', 'Muito grave']}
                  />
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-center text-lg font-semibold text-slate-900">Avaliação da fadiga</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">
                  As questões a seguir estão relacionadas com como você se sente <span className="font-semibold">cansado ou esgotado</span>. Escolha a resposta mais precisa.
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="block">
                  <span className="text-sm">1. Você já se sentiu cansado ou esgotado?</span>
                  <div className="mt-2 flex gap-4 text-sm">
                    <label className="inline-flex items-center gap-1"><input type="radio" name="pfsdq_fq1" checked={fQ1 === true} onChange={() => setFQ1(true)} /> Sim</label>
                    <label className="inline-flex items-center gap-1"><input type="radio" name="pfsdq_fq1" checked={fQ1 === false} onChange={() => setFQ1(false)} /> Não</label>
                  </div>
                </label>
                <label className="block">
                  <span className="text-sm">2. Quantas vezes por mês você sente cansaço grave a muito grave?</span>
                  <input type="number" min={0} className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2" value={fQ2} onChange={(event) => setFQ2(event.target.value)} />
                </label>
              </div>

              <div className="space-y-4">
                <p className="text-sm text-slate-700">Usando a escala a seguir, marque de 0 (sem cansaço) a 10 (cansaço muito grave) nas questões abaixo:</p>
                <div>
                  <div className="mb-1 text-sm">3. Como você se sentiu na <span className="font-semibold">maioria dos dias</span> durante o ano passado?</div>
                  <GeneralQuestionScale
                    name="pfsdq_fq3"
                    value={fQ3}
                    onChange={setFQ3}
                    legend={['Sem cansaço', 'Leve', 'Moderado', 'Grave', 'Muito grave']}
                  />
                </div>
                <div>
                  <div className="mb-1 text-sm">4. Como você se sente <span className="font-semibold">hoje</span>?</div>
                  <GeneralQuestionScale
                    name="pfsdq_fq4"
                    value={fQ4}
                    onChange={setFQ4}
                    legend={['Sem cansaço', 'Leve', 'Moderado', 'Grave', 'Muito grave']}
                  />
                </div>
                <div>
                  <div className="mb-1 text-sm">5. Como você se sente na <span className="font-semibold">maioria das atividades do dia a dia</span>?</div>
                  <GeneralQuestionScale
                    name="pfsdq_fq5"
                    value={fQ5}
                    onChange={setFQ5}
                    legend={['Sem cansaço', 'Leve', 'Moderado', 'Grave', 'Muito grave']}
                  />
                </div>
              </div>

              <div>
                <h3 className="text-center text-lg font-semibold text-slate-900">Grau de fadiga nas atividades</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">
                  Classifique as atividades de 0 a 10 de acordo com o grau de cansaço que cada uma geralmente causa. Deixe em branco atividades com que você nunca esteve envolvido.
                </p>
              </div>

              <div className="space-y-4">
                {PFSDQ_ACTIVITIES.map((activity, index) => (
                  <ActivityRow
                    key={`fatigue-${activity}`}
                    index={index}
                    label={activity}
                    value={fAct[index]}
                    onChange={(value) => setFAct((current) => current.map((item, itemIndex) => (itemIndex === index ? value : item)))}
                    headerLabels={['Nenhuma', 'Leve', 'Moderada', 'Grave', 'Muito grave']}
                  />
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-center text-lg font-semibold text-slate-900">Avaliação da mudança nas atividades</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">
                  Para cada atividade, marque de 0 a 10 como está seu envolvimento agora em comparação a antes do problema respiratório. Se nunca esteve envolvido com a atividade, use a opção correspondente.
                </p>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                0 = tão ativo como sempre • 1–3 = pequena mudança • 4–6 = mudança moderada • 7–9 = mudança extrema • 10 = não faz mais essa atividade
              </div>

              <div className="space-y-4">
                {PFSDQ_ACTIVITIES.map((activity, index) => (
                  <div key={`change-${activity}`} className="rounded-xl border border-slate-200 bg-white shadow-sm">
                    <div className="flex flex-col gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-sm font-medium text-slate-800">{index + 1}. {activity}</span>
                      <label className="inline-flex items-center gap-1 text-xs text-slate-600">
                        <input
                          type="checkbox"
                          checked={changeActivities[index].never}
                          onChange={(event) =>
                            setChangeActivities((current) =>
                              current.map((entry, entryIndex) =>
                                entryIndex === index
                                  ? { ...entry, never: event.target.checked, value: event.target.checked ? null : entry.value }
                                  : entry,
                              ),
                            )
                          }
                        />
                        Nunca estive envolvido com essa atividade
                      </label>
                    </div>
                    <div className="space-y-1.5 p-3">
                      <div className="grid grid-cols-6 gap-2 text-[10px] text-slate-600">
                        <div className="text-center">Tão ativo como sempre (0)</div>
                        <div className="text-center">Pequena mudança (1–3)</div>
                        <div className="text-center">Mudança moderada (4–6)</div>
                        <div className="text-center">Mudança extrema (7–9)</div>
                        <div className="col-span-2 text-center">Não faz mais essa atividade (10)</div>
                      </div>
                      <div className="grid grid-cols-11 text-xs text-slate-500">
                        {Array.from({ length: 11 }).map((_, number) => (
                          <div key={number} className="text-center">
                            {number}
                          </div>
                        ))}
                      </div>
                      <Scale0to10
                        name={`pfsdq_change_${index}`}
                        value={changeActivities[index].value}
                        onChange={(value) =>
                          setChangeActivities((current) =>
                            current.map((entry, entryIndex) => (entryIndex === index ? { ...entry, value } : entry)),
                          )
                        }
                        disabled={changeActivities[index].never}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4 border-t border-slate-200 px-4 py-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <button type="button" className="text-sm text-gray-600 hover:text-gray-800" onClick={onCancel}>
              Cancelar
            </button>
            <div className="text-sm text-slate-600">
              Parciais:
              <span className="ml-2 font-medium text-slate-900">Dispneia {dyspneaScore}</span>
              <span className="mx-2 text-slate-300">•</span>
              <span className="font-medium text-slate-900">Fadiga {fatigueScore}</span>
              <span className="mx-2 text-slate-300">•</span>
              <span className="font-medium text-slate-900">Mudança {changeScore}</span>
              <span className="mx-2 text-slate-300">•</span>
              <span className="font-semibold text-slate-900">Total {total}</span>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              className="inline-flex items-center rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-gray-200 disabled:opacity-50"
              onClick={() => setStep((current) => (current > 0 ? ((current - 1) as 0 | 1 | 2) : current))}
              disabled={step === 0}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Anterior
            </button>
            {step < 2 ? (
              <button
                type="button"
                className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
                onClick={() => setStep((current) => (current < 2 ? ((current + 1) as 0 | 1 | 2) : current))}
              >
                Próxima
                <ChevronRight className="ml-1 h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700"
                onClick={() =>
                  onNext({
                    dyspnea: {
                      general: { q1: dQ1, q2_per_month: dQ2, q3: dQ3, q4: dQ4, q5: dQ5 },
                      activities: dAct,
                      score: dyspneaScore,
                    },
                    fatigue: {
                      general: { q1: fQ1, q2_per_month: fQ2, q3: fQ3, q4: fQ4, q5: fQ5 },
                      activities: fAct,
                      score: fatigueScore,
                    },
                    change: {
                      activities: changeActivities,
                      score: changeScore,
                    },
                    manualCounts,
                    total,
                    finishedAt: new Date().toISOString(),
                  })
                }
              >
                Concluir PFSDQ-M
                <Check className="ml-1 h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
